import React, { useMemo, useCallback } from 'react';

function parseHM(timeStr) {
  const parts = String(timeStr || '0:0').split(':');
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  return { h: Number.isFinite(h) ? h : 0, m: Number.isFinite(m) ? m : 0 };
}

function parseHmToMinutes(hm) {
  const { h, m } = parseHM(hm);
  return h * 60 + m;
}

function inferSlotIntervalMinutes(slots) {
  if (!slots || slots.length < 2) return 30;
  const a = parseHmToMinutes(slots[0].time);
  const b = parseHmToMinutes(slots[1].time);
  const d = b - a;
  if (d > 0 && d <= 24 * 60) return d;
  return 30;
}

function slotAbsMinutes(slot) {
  const sm = parseHmToMinutes(slot?.time);
  if (!Number.isFinite(sm)) return 0;
  const dayAdj = slot?.nextDay ? 24 * 60 : 0;
  return sm + dayAdj;
}

/** 軸上分鐘（可含隔日 +1440）→ 鐘面標籤；隔日鐘面加「隔日」前綴 */
function absMinutesToWallLabel(absM) {
  if (!Number.isFinite(absM)) return '';
  const next = absM >= 24 * 60;
  const m = next ? absM - 24 * 60 : absM;
  const hh = Math.floor(m / 60) % 24;
  const mm = Math.floor(m % 60);
  const t = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  return next ? `隔日${t}` : t;
}

/** 已訂／不開放／短租灰格等：可合併為單一視覺區塊 */
function isGrayUnavailableSlot(slot) {
  if (slot?.available) return false;
  const reason = String(slot.unavailableReason || '').trim();
  const barTitle = String(slot.barSlotTitle || '').trim();
  if (reason === 'booked' || reason === 'blocked') return true;
  if (!reason && !barTitle) return true;
  return false;
}

/** 跨日僅提示（透明 title）：不併入灰區 */
function isHintOnlyUnavailableSlot(slot) {
  if (slot?.available) return false;
  const reason = String(slot.unavailableReason || '').trim();
  const barTitle = String(slot.barSlotTitle || '').trim();
  return reason !== 'booked' && reason !== 'blocked' && Boolean(barTitle);
}

function grayRunKindLabel(slots, startIdx, endIdx) {
  let hasBooked = false;
  let hasBlocked = false;
  let hasPlain = false;
  for (let i = startIdx; i <= endIdx; i++) {
    const r = String(slots[i]?.unavailableReason || '').trim();
    if (r === 'booked') hasBooked = true;
    else if (r === 'blocked') hasBlocked = true;
    else hasPlain = true;
  }
  const parts = [];
  if (hasBooked) parts.push('場地活動安排');
  if (hasBlocked) parts.push('不開放');
  if (hasPlain) parts.push('不可預約');
  return parts.length ? parts.join('／') : '不可預約';
}

function minDeltaPct(a, b) {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return Infinity;
  return Math.abs(a - b);
}

/** 起迄索引：selectedEnd 為「預約結束邊界」。
 *  @param {0|1|undefined} endDayOffset 短租：0＝結束鐘面在進場日；1＝隔日離場。未傳時維持舊行為（含隔日格則視為隔日鐘面，與跨日進場 bar 一致）。 */
function resolveRangeIndices(slots, selectedStart, selectedEnd, slotIntervalMinutes, endDayOffset) {
  const n = slots?.length || 0;
  if (!n) return { startIdx: -1, endIdx: -1, hasRange: false };

  const startIdx = slots.findIndex((s) => s.time === selectedStart && !s.nextDay);
  const interval = slotIntervalMinutes || inferSlotIntervalMinutes(slots);
  const endParsed = selectedEnd != null ? parseHmToMinutes(selectedEnd) : NaN;
  const hasNextDaySlot = slots.some((s) => s.nextDay);
  let endBoundaryAbs = NaN;
  if (Number.isFinite(endParsed)) {
    const onNext = endDayOffset === 1 || (endDayOffset === undefined && hasNextDaySlot);
    endBoundaryAbs = onNext ? 24 * 60 + endParsed : endParsed;
  }

  let endIdx = -1;
  if (startIdx >= 0 && Number.isFinite(endBoundaryAbs)) {
    for (let i = startIdx; i < n; i++) {
      const sm = parseHmToMinutes(slots[i].time);
      if (!Number.isFinite(sm)) continue;
      const dayAdj = slots[i].nextDay ? 24 * 60 : 0;
      const cellEnd = sm + dayAdj + interval;
      if (cellEnd <= endBoundaryAbs) endIdx = i;
    }
  }

  const hasRange = startIdx >= 0 && endIdx >= 0 && endIdx >= startIdx;
  return { startIdx, endIdx, hasRange, endBoundaryAbs };
}

/** 僅在關鍵索引顯示標籤：起迄、整點；過多時抽樣，避免重疊 */
function buildLabelIndices(slots) {
  const n = slots.length;
  if (n === 0) return [];
  if (n === 1) return [0];
  const set = new Set([0, n - 1]);
  for (let i = 0; i < n; i++) {
    const { m } = parseHM(slots[i].time);
    if (m === 0) set.add(i);
  }
  let arr = [...set].sort((a, b) => a - b);
  const maxLabels = 12;
  if (arr.length > maxLabels) {
    const thin = [0];
    const inner = arr.slice(1, -1);
    const need = maxLabels - 2;
    const step = inner.length <= need ? 1 : Math.ceil(inner.length / need);
    for (let i = 0; i < inner.length; i += step) thin.push(inner[i]);
    thin.push(n - 1);
    arr = [...new Set(thin)].sort((a, b) => a - b);
  }
  return arr;
}

/** 整點只顯示小時數字；起迄非整點則顯示 HH:mm；nextDay 格加「隔日」前綴 */
function formatTickLabel(slot, idx, n) {
  const t = String(slot.time || '');
  const { h, m } = parseHM(t);
  const prefix = slot.nextDay ? '隔日' : '';
  if (idx === 0 || idx === n - 1) {
    if (m === 0) return `${prefix}${h}`;
    return `${prefix}${t}`;
  }
  return `${prefix}${h}`;
}

/**
 * 唯讀：顯示各時段是否可預約；已選起迄僅落在可選格，以綠底＋頭尾綠字標示（場地短租／跨日）。
 * @param {number} [slotIntervalMinutes] 與後台 timeSlotInterval 一致；省略時由前兩格推算
 * @param {string} [checkoutNote] 跨日等：條形圖下方顯示離場時間（綠字）
 * @param {{ start: string, end: string }} [crossDaySchematicLabels] 跨日時改為只顯示頭尾日期＋時間（示意，不按格點比例排刻度）
 * @param {{ start: string, end: string }} [checkinWindowBand] 跨日：在條形圖上方顯示「可選進場」區間與虛線
 * @param {number} [selectedMarkerIndex] 跨日：依目前選中的進場格顯示可動藍色標線（覆寫固定隔日界線）
 * @param {boolean} [hideNextDayBoundaryLine] 跨日：不顯示隔日交界固定藍線（改由 selectedMarkerIndex 表示選取）
 * @param {number} [axisStartMinutes] 與跨日 bar 對齊：軸起點（預約日 00:00 起算之分鐘，如最早進場 18:30 → 1110）
 * @param {number} [axisEndMinutes] 軸終點（同樣以預約日 00:00 起算，如隔日 12:00 → 2160）
 * @param {string} [axisEndLabel] 軸尾顯示文字（通常 = 離場鐘面時間 HH:mm）
 * @param {boolean} [nextDayBoundaryCentered] 跨日專用：條形左半為「進場日 axisStart→24:00」依時間比例，右半為「隔日 00:00→退房」依時間比例；00:00／隔日虛線固定在條形正中 50%。短租勿開此項，維持單段時間比例。
 * @param {0|1|undefined} [endDayOffset] 短租專用：見 resolveRangeIndices。
 * @param {(slot: object, idx: number) => void} [onSlotClick] 若傳入且 slot.clickable 為 true，該格可點選（例如短租進場）
 * @param {string} [slotPickAriaPrefix] 搭配 onSlotClick 的 aria-label 前綴（例如「進場」）
 */
export default function TimeAvailabilityBar({
  slots,
  selectedStart,
  selectedEnd,
  endDayOffset,
  slotIntervalMinutes,
  checkoutNote,
  crossDaySchematicLabels,
  tickLabelIndices,
  tickLabelFormatter,
  nextDayBoundaryLabel = '',
  nextDayBoundaryCentered = false,
  checkinWindowBand,
  selectedMarkerIndex,
  hideNextDayBoundaryLine = false,
  axisStartMinutes,
  axisEndMinutes,
  axisEndLabel,
  onSlotClick,
  slotPickAriaPrefix = '',
  primary = 'var(--vb-slot-selected-start, #52c41a)',
  primaryDark = 'var(--vb-slot-selected-end, #389e0d)',
  selectionMarkerColor = 'var(--vb-slot-selected-end, #389e0d)',
}) {
  const n = slots?.length || 0;
  const { startIdx, endIdx, hasRange, endBoundaryAbs } = useMemo(
    () => resolveRangeIndices(slots, selectedStart, selectedEnd, slotIntervalMinutes, endDayOffset),
    [slots, selectedStart, selectedEnd, slotIntervalMinutes, endDayOffset],
  );

  const labelIndices = useMemo(() => {
    let arr;
    if (Array.isArray(tickLabelIndices) && tickLabelIndices.length) {
      arr = [...new Set(tickLabelIndices)].filter((i) => Number.isFinite(i) && i >= 0 && i < n).sort((a, b) => a - b);
    } else {
      arr = (slots?.length ? buildLabelIndices(slots) : []);
    }
    if (hasRange) {
      if (startIdx >= 0 && startIdx < n && !arr.includes(startIdx)) {
        arr = [...arr, startIdx].sort((a, b) => a - b);
      }
      if (endIdx >= 0 && endIdx < n && !arr.includes(endIdx)) {
        arr = [...arr, endIdx].sort((a, b) => a - b);
      }
    }
    return arr;
  }, [slots, tickLabelIndices, n, hasRange, startIdx, endIdx]);

  const nextDayStartIdx = useMemo(
    () => (slots ? slots.findIndex((s) => s.nextDay) : -1),
    [slots],
  );

  const useAxis = Number.isFinite(axisStartMinutes) && Number.isFinite(axisEndMinutes) && axisEndMinutes > axisStartMinutes;
  const axisSpan = useAxis ? axisEndMinutes - axisStartMinutes : 0;

  const useSplitCrossDayAxis = Boolean(
    nextDayBoundaryCentered && nextDayStartIdx > 0 && useAxis,
  );

  const pctAtPosition = useCallback((absMin) => {
    if (!useAxis) return 0;
    const lo = axisStartMinutes;
    const hi = axisEndMinutes;
    const m = Number(absMin);
    if (!Number.isFinite(m)) return 0;
    if (useSplitCrossDayAxis) {
      const MID = 24 * 60;
      if (m <= MID) {
        const denom = MID - lo;
        if (denom <= 0) return 0;
        return Math.min(50, Math.max(0, ((m - lo) / denom) * 50));
      }
      const denomR = hi - MID;
      if (denomR <= 0) return 50;
      return Math.min(100, Math.max(50, 50 + ((m - MID) / denomR) * 50));
    }
    if (!axisSpan) return 0;
    return Math.min(100, Math.max(0, ((m - lo) / axisSpan) * 100));
  }, [useAxis, useSplitCrossDayAxis, axisStartMinutes, axisEndMinutes, axisSpan]);

  const pctOfSlot = (slot) => pctAtPosition(slotAbsMinutes(slot));

  const leftPct = (idx) => (useAxis ? pctOfSlot(slots[idx]) : (idx / n) * 100);
  const segWidthPct = (idx) => {
    if (!useAxis) return 100 / n;
    const s = slotAbsMinutes(slots[idx]);
    const interval = slotIntervalMinutes || inferSlotIntervalMinutes(slots);
    return pctAtPosition(s + interval) - pctAtPosition(s);
  };

  const intervalMinResolved = slotIntervalMinutes || inferSlotIntervalMinutes(slots);

  const unavailableGrayRuns = useMemo(() => {
    if (!slots?.length) return [];
    const nSlots = slots.length;
    const runs = [];
    let runStart = -1;
    for (let i = 0; i < nSlots; i++) {
      const slot = slots[i];
      if (isHintOnlyUnavailableSlot(slot)) {
        if (runStart >= 0) {
          runs.push({ startIdx: runStart, endIdx: i - 1 });
          runStart = -1;
        }
        continue;
      }
      if (isGrayUnavailableSlot(slot)) {
        if (runStart < 0) runStart = i;
      } else if (runStart >= 0) {
        runs.push({ startIdx: runStart, endIdx: i - 1 });
        runStart = -1;
      }
    }
    if (runStart >= 0) runs.push({ startIdx: runStart, endIdx: nSlots - 1 });
    return runs;
  }, [slots]);

  const hintOnlySlots = useMemo(() => {
    if (!slots?.length) return [];
    const out = [];
    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i];
      if (!isHintOnlyUnavailableSlot(slot)) continue;
      out.push({ idx: i, slot, barTitle: String(slot.barSlotTitle || '').trim() });
    }
    return out;
  }, [slots]);

  const markerIdx = Number.isFinite(selectedMarkerIndex) && selectedMarkerIndex >= 0 && selectedMarkerIndex < n
    ? selectedMarkerIndex
    : -1;

  const labelHighlightStartIdx = hasRange ? startIdx : markerIdx;
  const labelHighlightEndIdx = hasRange ? endIdx : -1;

  const rangeLeftPct = hasRange ? leftPct(startIdx) : 0;
  const rangeWidthPct = hasRange
    ? (
        useAxis
          ? (
              pctAtPosition(
                slotAbsMinutes(slots[endIdx]) + (slotIntervalMinutes || inferSlotIntervalMinutes(slots)),
              ) - rangeLeftPct
            )
          : (((endIdx - startIdx + 1) / n) * 100)
      )
    : 0;

  const nextDayBoundaryPct = useMemo(() => {
    if (useSplitCrossDayAxis) return 50;
    if (useAxis) return pctAtPosition(24 * 60);
    return nextDayStartIdx > 0 ? (nextDayStartIdx / n) * 100 : 0;
  }, [useSplitCrossDayAxis, useAxis, nextDayStartIdx, n, pctAtPosition]);

  const filteredLabelIndices = useMemo(() => {
    if (!labelIndices.length) return [];
    if (!useAxis || !axisSpan) return labelIndices;

    const minGap = 4.2; // %：避免文字重疊（保守值）
    const keep = [];

    const idxWithPct = labelIndices.map((idx) => ({
      idx,
      pct: pctAtPosition(slotAbsMinutes(slots[idx])),
      isEdge: idx === 0 || idx === n - 1,
      isHighlight: (labelHighlightStartIdx >= 0 && idx === labelHighlightStartIdx)
        || (labelHighlightEndIdx >= 0 && idx === labelHighlightEndIdx),
    })).sort((a, b) => a.pct - b.pct);

    for (const it of idxWithPct) {
      /** 已選起迄必須保留，避免軸端附近 pct 被當成「貼邊雜訊」濾掉 */
      if (!it.isEdge && !it.isHighlight && (it.pct < 1.5 || it.pct > 98.5)) continue;

      const last = keep.length ? keep[keep.length - 1] : null;
      if (!last) {
        keep.push(it);
        continue;
      }

      const tooClose = minDeltaPct(it.pct, last.pct) < minGap;
      if (!tooClose) {
        keep.push(it);
        continue;
      }

      // 太近時：保留 highlight，其次保留 edge
      if (it.isHighlight && !last.isHighlight) {
        keep[keep.length - 1] = it;
        continue;
      }
      if (it.isEdge && !last.isEdge && !last.isHighlight) {
        keep[keep.length - 1] = it;
        continue;
      }
      // 其他情況：跳過目前這個（避免重疊）
    }

    return keep.map((k) => k.idx);
  }, [labelIndices, useAxis, axisSpan, axisStartMinutes, slots, n, labelHighlightStartIdx, labelHighlightEndIdx, pctAtPosition]);

  /** 已訂／不可選格上不畫鐘面字（避免與灰底混淆）；軸端與已選起迄仍顯示 */
  const tickLabelRowIndices = useMemo(
    () => filteredLabelIndices.filter((idx) => {
      const slot = slots[idx];
      const isEdge = idx === 0 || idx === n - 1;
      const isHighlight = (labelHighlightStartIdx >= 0 && idx === labelHighlightStartIdx)
        || (labelHighlightEndIdx >= 0 && idx === labelHighlightEndIdx);
      if (isEdge || isHighlight) return true;
      if (slot?.available) return true;
      /** 跨日：僅隔日檔期衝突時不畫斜線，仍顯示鐘面字 */
      const noStripe = !slot?.nextDay && !slot?.available && !String(slot?.unavailableReason || '').trim();
      return Boolean(noStripe);
    }),
    [filteredLabelIndices, slots, n, labelHighlightStartIdx, labelHighlightEndIdx],
  );

  /** 灰區（已訂／不開放）左右邊界節點：強制顯示文字，與包場間格後實際封閉區間對齊 */
  const grayRunBoundaryLabels = useMemo(() => {
    if (crossDaySchematicLabels || !unavailableGrayRuns.length || !n) return [];
    const fmtBoundary = (absM) => {
      if (!Number.isFinite(absM)) return '';
      const next = absM >= 24 * 60;
      const m = next ? absM - 24 * 60 : absM;
      const mm = m % 60;
      const hh = Math.floor(m / 60) % 24;
      const prefix = next ? '隔日' : '';
      if (mm === 0) return `${prefix}${hh}`;
      const t = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
      return `${prefix}${t}`;
    };
    const out = [];
    const usedPct = [];
    const tryPush = (pct, label, key) => {
      if (!Number.isFinite(pct) || pct < -0.01 || pct > 100.01 || !label) return;
      for (const u of usedPct) {
        if (minDeltaPct(pct, u) < 2.8) return;
      }
      usedPct.push(pct);
      out.push({ pct, label, key });
    };
    for (const run of unavailableGrayRuns) {
      const stAbs = slotAbsMinutes(slots[run.startIdx]);
      const enAbs = slotAbsMinutes(slots[run.endIdx]) + intervalMinResolved;
      const pL = useAxis ? pctAtPosition(stAbs) : (run.startIdx / n) * 100;
      const pR = useAxis ? pctAtPosition(enAbs) : ((run.endIdx + 1) / n) * 100;
      tryPush(pL, fmtBoundary(stAbs), `gry-lo-${run.startIdx}-${run.endIdx}`);
      tryPush(pR, fmtBoundary(enAbs), `gry-hi-${run.startIdx}-${run.endIdx}`);
    }
    return out.sort((a, b) => a.pct - b.pct);
  }, [unavailableGrayRuns, slots, n, crossDaySchematicLabels, useAxis, pctAtPosition, intervalMinResolved]);

  const pctLayout = (p) => {
    if (p <= 0.01) {
      return { left: '0%', transform: 'translateX(0)', textAlign: 'left' };
    }
    if (p >= 99.99) {
      return { left: '100%', transform: 'translateX(-100%)', textAlign: 'right' };
    }
    return { left: `${p}%`, transform: 'translateX(-50%)', textAlign: 'center' };
  };

  if (!n) return null;

  return (
    <div style={{ padding: '0 4px 6px' }}>
      {checkinWindowBand?.start && checkinWindowBand?.end ? (
        <div
          style={{
            marginBottom: 8,
            padding: '6px 8px',
            borderRadius: 8,
            background: 'linear-gradient(180deg, #f4faf4 0%, #e8f5e9 100%)',
            border: '1px dashed rgba(56, 142, 60, 0.45)',
            fontSize: 12,
            color: '#434343',
            lineHeight: 1.45,
          }}
        >
          <span style={{ fontWeight: 700, color: '#237804' }}>可選擇進場</span>
          <span style={{ margin: '0 6px', opacity: 0.45 }}>｜</span>
          <span>
            {checkinWindowBand.start} <span style={{ opacity: 0.5 }}>～</span> {checkinWindowBand.end}
          </span>
        </div>
      ) : null}

      {nextDayStartIdx > 0 && !hideNextDayBoundaryLine && nextDayBoundaryLabel ? (
        <div style={{ position: 'relative', height: 18, marginBottom: 2 }}>
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: `${nextDayBoundaryPct}%`,
              transform: 'translateX(-50%)',
              top: 0,
              fontSize: 11,
              fontWeight: 600,
              color: 'rgba(24, 144, 255, 0.9)',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            {nextDayBoundaryLabel}
          </span>
        </div>
      ) : null}

      <div
        style={{
          position: 'relative',
          height: 34,
          marginBottom: 6,
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,.05)',
          border: '1px solid rgba(0,0,0,.06)',
        }}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: '#ffffff',
          }}
        />

        {startIdx >= 0 && !hasRange && markerIdx < 0 && (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: 5,
              bottom: 5,
              left: `${leftPct(startIdx)}%`,
              transform: 'translateX(0)',
              width: 4,
              borderRadius: '0 2px 2px 0',
              background: `linear-gradient(180deg, ${primary}, ${primaryDark})`,
              zIndex: 2,
              boxShadow: '0 0 0 1px rgba(255,255,255,0.35)',
            }}
          />
        )}

        {unavailableGrayRuns.map((run) => {
          const runLeft = leftPct(run.startIdx);
          const runRight = leftPct(run.endIdx) + segWidthPct(run.endIdx);
          const runW = Math.max(0, runRight - runLeft);
          const kindText = grayRunKindLabel(slots, run.startIdx, run.endIdx);
          const stAbs = slotAbsMinutes(slots[run.startIdx]);
          const enAbs = slotAbsMinutes(slots[run.endIdx]) + intervalMinResolved;
          const rangeText = `${absMinutesToWallLabel(stAbs)}–${absMinutesToWallLabel(enAbs)}`;
          return (
            <div
              key={`gray-run-${run.startIdx}-${run.endIdx}`}
              aria-hidden
              title={`${rangeText} ${kindText}`}
              style={{
                position: 'absolute',
                top: 4,
                bottom: 4,
                left: `${runLeft}%`,
                width: `${runW}%`,
                background: 'repeating-linear-gradient(135deg, #dcdcdc 0, #dcdcdc 2.5px, #ebebeb 2.5px, #ebebeb 5px)',
                zIndex: 3,
                boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.04)',
                pointerEvents: 'none',
                borderRadius: 6,
              }}
            />
          );
        })}

        {hintOnlySlots.map(({ idx, slot, barTitle }) => {
          const w = segWidthPct(idx);
          return (
            <div
              key={`hint-${slot.time}-${slot.nextDay ? 'nd' : ''}-${idx}`}
              title={barTitle}
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${leftPct(idx)}%`,
                width: `${w}%`,
                zIndex: 2,
                cursor: 'default',
                pointerEvents: 'auto',
              }}
            />
          );
        })}

        {typeof onSlotClick === 'function' &&
          slots.map((slot, idx) => {
            if (!slot?.clickable) return null;
            const w = segWidthPct(idx);
            const lp = leftPct(idx);
            const aria = [String(slotPickAriaPrefix || '').trim(), String(slot.time || '').trim()].filter(Boolean).join(' ');
            return (
              <button
                key={`slot-pick-${idx}-${slot.time}-${slot.nextDay ? 'nd' : 'd0'}`}
                type="button"
                aria-label={aria || String(slot.time)}
                onClick={() => onSlotClick(slot, idx)}
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${lp}%`,
                  width: `${Math.max(w, 0.15)}%`,
                  zIndex: 4,
                  cursor: 'pointer',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  background: 'transparent',
                }}
              />
            );
          })}

        {/* 已選僅涵蓋可選格（與灰底不重疊）；zIndex 僅與白底／刻度區隔 */}
        {hasRange && (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: 4,
              bottom: 4,
              left: `${rangeLeftPct}%`,
              width: `${rangeWidthPct}%`,
              background: '#e8f5e9',
              borderRadius: 6,
              zIndex: 14,
              boxShadow: 'inset 0 0 0 1px rgba(56, 142, 60, 0.35)',
            }}
          />
        )}

        {markerIdx >= 0 && (
          <div
            aria-hidden
            title="目前選擇的進場時間"
            style={{
              position: 'absolute',
              top: 3,
              bottom: 3,
              left: `${leftPct(markerIdx)}%`,
              transform: 'translateX(0)',
              width: 4,
              borderRadius: '0 2px 2px 0',
              background: selectionMarkerColor,
              zIndex: 5,
              boxShadow: '0 0 0 2px rgba(56, 142, 60, 0.25)',
            }}
          />
        )}

        {nextDayStartIdx > 0 && !hideNextDayBoundaryLine && (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: 2,
              bottom: 2,
              left: `${nextDayBoundaryPct}%`,
              width: 1,
              background: 'rgba(0,0,0,0.12)',
              zIndex: useSplitCrossDayAxis ? 5 : 3,
              pointerEvents: 'none',
            }}
          />
        )}

        {nextDayStartIdx > 0 && !hideNextDayBoundaryLine && nextDayBoundaryLabel ? (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: 2,
              bottom: 2,
              left: `${nextDayBoundaryPct}%`,
              width: 2,
              borderLeft: '2px dashed rgba(24, 144, 255, 0.55)',
              zIndex: useSplitCrossDayAxis ? 5 : 3,
              pointerEvents: 'none',
            }}
          />
        ) : null}

        {/* 刻度線 */}
        {crossDaySchematicLabels ? null : (
          labelIndices.map((idx) => (
            <div
              key={`tk-${idx}-${slots[idx]?.time}`}
              aria-hidden
              style={{
                position: 'absolute',
                top: 6,
                bottom: 6,
                left: `${leftPct(idx)}%`,
                width: 1,
                background: 'rgba(0,0,0,.10)',
                zIndex: 4,
                pointerEvents: 'none',
              }}
            />
        ))
        )}

      </div>

      <div
        style={{
          position: 'relative',
          minHeight: 22,
          marginTop: 3,
          color: 'var(--venue-text-muted, #888)',
          fontSize: 11,
          /** 與上方條同寬；文字用與條相同的 % 定位，不另做內縮以免與條形錯位 */
          overflow: 'visible',
        }}
      >
        {crossDaySchematicLabels?.start != null && crossDaySchematicLabels?.end != null ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              lineHeight: 1.35,
            }}
          >
            <span style={{ flex: '1 1 42%', textAlign: 'left', whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {crossDaySchematicLabels.start}
            </span>
            <span style={{ flex: '0 0 auto', opacity: 0.4, letterSpacing: 1 }}>···</span>
            <span style={{ flex: '1 1 42%', textAlign: 'right', whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {crossDaySchematicLabels.end}
            </span>
          </div>
        ) : (
          <>
            {tickLabelRowIndices.map((idx) => {
              const isStartHi = labelHighlightStartIdx >= 0 && idx === labelHighlightStartIdx;
              const isEndHi = labelHighlightEndIdx >= 0 && idx === labelHighlightEndIdx;
              if (useAxis && axisEndLabel && idx === n - 1 && !isEndHi) return null;
              const p = (useAxis && isEndHi && Number.isFinite(endBoundaryAbs))
                ? pctAtPosition(endBoundaryAbs)
                : (useAxis ? pctAtPosition(slotAbsMinutes(slots[idx])) : (idx / n) * 100);
              const isFirst = idx === 0;
              const isLast = idx === n - 1;
              let left;
              let transform;
              let textAlign;
              if (useAxis) {
                if (isEndHi && Number.isFinite(endBoundaryAbs)) {
                  if (p <= 0.01) {
                    left = '0%';
                    transform = 'translateX(0)';
                    textAlign = 'left';
                  } else if (p >= 99.99) {
                    left = '100%';
                    transform = 'translateX(-100%)';
                    textAlign = 'right';
                  } else {
                    left = `${p}%`;
                    transform = 'translateX(-50%)';
                    textAlign = 'center';
                  }
                } else if (p <= 0.01) {
                  left = '0%';
                  transform = 'translateX(0)';
                  textAlign = 'left';
                } else if (p >= 99.99) {
                  left = '100%';
                  transform = 'translateX(-100%)';
                  textAlign = 'right';
                } else {
                  left = `${p}%`;
                  transform = 'translateX(-50%)';
                  textAlign = 'center';
                }
              } else {
                left = isFirst ? '0%' : `${(idx / n) * 100}%`;
                transform = isFirst ? 'translateX(0)' : (isLast ? 'translateX(-100%)' : 'translateX(0)');
                textAlign = isFirst ? 'left' : (isLast ? 'right' : 'left');
              }
              const isHighlight = isStartHi || isEndHi;
              let labelBody;
              if (hasRange && isStartHi && isEndHi && selectedStart && selectedEnd) {
                labelBody = (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'baseline' }}>
                    <span>{String(selectedStart).trim()}</span>
                    <span style={{ opacity: 0.45 }}>·</span>
                    <span>{String(selectedEnd).trim()}</span>
                  </span>
                );
              } else if (hasRange && isStartHi && selectedStart) {
                labelBody = String(selectedStart).trim();
              } else if (hasRange && isEndHi && selectedEnd) {
                labelBody = String(selectedEnd).trim();
              } else if (typeof tickLabelFormatter === 'function') {
                labelBody = tickLabelFormatter(slots[idx], idx, n);
              } else {
                labelBody = formatTickLabel(slots[idx], idx, n);
              }
              return (
                <span
                  key={`lb-${idx}-${slots[idx]?.time}`}
                  style={{
                    position: 'absolute',
                    left,
                    transform,
                    textAlign,
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    letterSpacing: 0.2,
                    color: isHighlight ? '#237804' : 'rgba(120, 118, 112, 0.88)',
                    fontWeight: isHighlight ? 700 : 500,
                    zIndex: isHighlight ? 6 : 1,
                    background: isHighlight ? 'rgba(255,255,255,0.92)' : 'transparent',
                    padding: isHighlight ? '0 3px' : 0,
                    borderRadius: isHighlight ? 4 : 0,
                  }}
                >
                  {labelBody}
                </span>
              );
            })}
            {grayRunBoundaryLabels.map(({ pct, label, key }) => {
              const { left, transform, textAlign } = pctLayout(pct);
              return (
                <span
                  key={key}
                  style={{
                    position: 'absolute',
                    left,
                    transform,
                    textAlign,
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    letterSpacing: 0.2,
                    fontWeight: 700,
                    color: 'rgba(74, 61, 53, 0.95)',
                    zIndex: 8,
                    background: 'rgba(255,255,255,0.9)',
                    padding: '0 3px',
                    borderRadius: 4,
                  }}
                >
                  {label}
                </span>
              );
            })}
            {useAxis && axisEndLabel ? (
              <span
                key="lb-axis-end"
                style={{
                  position: 'absolute',
                  left: '100%',
                  transform: 'translateX(-100%)',
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                  letterSpacing: 0.2,
                  /** 軸尾＝營業／離場邊界，非已選起迄：不因 hasRange 變綠（已選頭尾仍由刻度列高亮） */
                  color: 'rgba(120, 118, 112, 0.88)',
                  fontWeight: 500,
                  zIndex: 1,
                }}
              >
                {slots.some((s) => s.nextDay) ? `隔日${axisEndLabel}` : axisEndLabel}
              </span>
            ) : null}
          </>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 12,
          marginTop: 8,
          fontSize: 11,
          color: 'var(--venue-text-muted, #888)',
          justifyContent: 'center',
          flexWrap: 'wrap',
          rowGap: 4,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span
            style={{
              width: 16,
              height: 6,
              borderRadius: 3,
              background: '#ffffff',
              border: '1px solid #e4ddd3',
            }}
          />
          可預約
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span
            style={{
              width: 16,
              height: 6,
              borderRadius: 3,
              background: '#e8e8e8',
              border: '1px solid #d8d8d8',
            }}
          />
          不可預約／不開放
        </span>
        {hasRange && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span
              style={{
                width: 16,
                height: 6,
                borderRadius: 3,
                background: '#e8f5e9',
                border: '1px solid rgba(56, 142, 60, 0.35)',
              }}
            />
            已選區間
          </span>
        )}
      </div>
      {checkoutNote ? (
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            fontWeight: 600,
            color: '#237804',
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          {checkoutNote}
        </div>
      ) : null}
    </div>
  );
}
